import { memo, useCallback, useMemo } from 'react';

import { PauseIcon, RefreshCwIcon, UploadIcon } from '@air/design-icons';
import { completedUploadCountSelector, failedUploadCountSelector } from '@air/redux-uploader';

import {
  MAIN_SIDE_NAV_SMALL_ICON_SIZE,
  MainSideNavItemButton,
} from '~/components/v2/components/workspace/main-side-nav/item/MainSideNavItemButton';
import { MainSideNavItemCountBadge } from '~/components/v2/components/workspace/main-side-nav/item/MainSideNavItemCountBadge';
import { MainSideNavItemIcon } from '~/components/v2/components/workspace/main-side-nav/item/MainSideNavItemIcon';
import { MainSideNavItemLabel } from '~/components/v2/components/workspace/main-side-nav/item/MainSideNavItemLabel';
import { useMainSideNavOverlay } from '~/components/v2/components/workspace/main-side-nav/useMainSideNavOverlay';
import { useWorkspaceUploadsInProgressCounts } from '~/components/v2/components/workspace/workspace-uploads/useWorkspaceUploadsInProgressCounts';
import { useWorkspaceUploadsProgressingLabel } from '~/components/v2/components/workspace/workspace-uploads/useWorkspaceUploadsProgressingLabel';
import { WORKSPACE_UPLOADS_TRAY_ID } from '~/constants/workspaceActivity';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { canCreateAsset } from '~/utils/permissions/assetPermissions';
import { useAirSelector } from '~/utils/ReduxUtils';

export const MainSideNavItemUploads = memo(() => {
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const { isOpen, onOpenChange } = useMainSideNavOverlay({ id: 'uploads' });
  const canUpload = canCreateAsset(workspacePermissions);
  const completedCount = useAirSelector(completedUploadCountSelector);
  const failedCount = useAirSelector(failedUploadCountSelector);
  const hasFailedUploads = failedCount > 0;
  const { hasInProgressUploads, hasProgressingUploads, inProgressCount, isPaused } =
    useWorkspaceUploadsInProgressCounts();
  const isCompleted = completedCount > 0 && !hasInProgressUploads && !hasFailedUploads;
  const { progressingLabel } = useWorkspaceUploadsProgressingLabel();

  const navLabel = useMemo(() => {
    if (hasFailedUploads) {
      return 'Upload Problem';
    }

    if (isPaused) {
      return 'Upload Paused';
    }

    if (isCompleted) {
      return 'Upload Complete';
    }

    return progressingLabel;
  }, [hasFailedUploads, isCompleted, isPaused, progressingLabel]);

  const ariaLabel = useMemo(() => {
    if (hasFailedUploads) {
      return 'Upload Problem';
    }

    if (isCompleted) {
      return 'Upload Complete';
    }

    if (hasInProgressUploads) {
      return `Uploads, ${navLabel}`;
    }

    return 'Uploads';
  }, [hasFailedUploads, hasInProgressUploads, isCompleted, navLabel]);

  const handleClick = useCallback(() => {
    onOpenChange(!isOpen);
  }, [isOpen, onOpenChange]);

  if (!canUpload) {
    return null;
  }

  return (
    <MainSideNavItemButton
      aria-controls={WORKSPACE_UPLOADS_TRAY_ID}
      aria-expanded={isOpen}
      aria-haspopup="dialog"
      aria-label={ariaLabel}
      data-item-id="uploads"
      data-main-side-nav-overlay
      data-state={isOpen ? 'open' : undefined}
      data-testid="WORKSPACE_UPLOADS_BUTTON"
      onClick={handleClick}
      type="button"
    >
      <MainSideNavItemIcon>
        <span className="relative" data-finished-batch-offer-anchor="">
          {!hasFailedUploads && hasInProgressUploads && isPaused ? (
            <PauseIcon className="shrink-0" size={MAIN_SIDE_NAV_SMALL_ICON_SIZE} />
          ) : null}
          {!hasFailedUploads && hasProgressingUploads ? (
            <RefreshCwIcon
              className="shrink-0 animate-spin motion-reduce:animate-none"
              size={MAIN_SIDE_NAV_SMALL_ICON_SIZE}
            />
          ) : null}
          {hasFailedUploads || !hasInProgressUploads ? (
            <UploadIcon className="shrink-0" size={MAIN_SIDE_NAV_SMALL_ICON_SIZE} />
          ) : null}
          {!hasFailedUploads && isCompleted ? (
            <span
              aria-hidden
              className="absolute -top-0.5 -right-0.5 size-1.5 rounded-full ads:bg-ads-grass-9 bg-grass-9"
              data-testid="WORKSPACE_UPLOADS_BUTTON_COMPLETE_DOT"
            />
          ) : null}
          {hasFailedUploads ? (
            <MainSideNavItemCountBadge
              count={failedCount}
              data-testid="WORKSPACE_UPLOADS_BUTTON_ERROR_BADGE"
              tone="warning"
            />
          ) : null}
          {!hasFailedUploads && hasProgressingUploads ? (
            <MainSideNavItemCountBadge count={inProgressCount} data-testid="WORKSPACE_UPLOADS_BUTTON_BADGE" />
          ) : null}
        </span>
      </MainSideNavItemIcon>
      <MainSideNavItemLabel>
        {hasFailedUploads || hasInProgressUploads || isCompleted ? navLabel : 'Uploads'}
      </MainSideNavItemLabel>
    </MainSideNavItemButton>
  );
});

MainSideNavItemUploads.displayName = 'MainSideNavItemUploads';
